import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { Badge, Button, Card, DatePicker, Divider, Radio } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import {
  BREAKPOINTS,
  DATE_FORMATS,
  MODULES,
  STATUS,
  USER_FORM_STATUS,
  USER_ROLES,
} from '../../../common/constants';
import { formatDate } from '../../../common/utils';
import CommonTable from '../../../components/CommonTable';
import Portal from '../../../components/Portal';
import SearchComponent from '../../../components/SearchComponent';
import {
  ALL_STAFF_ROLES,
  STAFF_ROLE_FILTER_OPTIONS,
  getStaffRoleLabel,
} from '../../../modules/users/modules/staff/staffConfig';
import StaffAvailabilitySwitch from '../../../modules/users/modules/staff/components/StaffAvailabilitySwitch';
import { MY_LIBRARY_STAFF } from '../graphql/Queries';

const { RangePicker } = DatePicker;
const { Group } = Radio;

const LibraryStaff = () => {
  const [query, setQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [draftFilters, setDraftFilters] = useState({});

  const { data: { library } = {}, loading, refetch } = useQuery(
    MY_LIBRARY_STAFF,
    {
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const search = searchTerm?.trim()?.toLowerCase();
  const staffList = (library?.staff || [])
    ?.filter((staff) => !filters?.role || staff?.role === filters?.role)
    ?.filter((staff) => {
      if (!search) return true;
      const { firstName, lastName, email, contactNo, userNumber } =
        staff?.user || {};
      return [
        `${firstName || ''} ${lastName || ''}`,
        email,
        contactNo,
        userNumber,
      ]?.some((value) => value?.toLowerCase()?.includes(search));
    })
    ?.filter((staff) => {
      const { from, to } = filters?.createdAt || {};
      if (from && dayjs(staff?.createdAt)?.isBefore(from)) return false;
      if (to && dayjs(staff?.createdAt)?.isAfter(to)) return false;
      return true;
    })
    ?.filter(
      (staff) =>
        filters?.status === undefined ||
        filters?.status === null ||
        staff?.user?.isActive === filters?.status,
    );

  const applyFilters = (updatedFilters, confirm) => {
    setFilters(updatedFilters);
    setDraftFilters(updatedFilters);
    if (confirm) confirm();
  };

  const filterPopup = (dataIndex) => ({
    filterDropdown: ({ confirm, clearFilters }) => (
      <div className="custom-filter-dropdown">
        {dataIndex === 'role' && (
          <Group
            className="d-flex flex-vertical"
            options={STAFF_ROLE_FILTER_OPTIONS}
            value={draftFilters?.role || ALL_STAFF_ROLES}
            onChange={(e) =>
              setDraftFilters({
                ...draftFilters,
                role:
                  e?.target?.value === ALL_STAFF_ROLES
                    ? undefined
                    : e?.target?.value,
              })
            }
          />
        )}
        {dataIndex === 'createdAt' && (
          <RangePicker
            allowClear={false}
            format={DATE_FORMATS?.REGULAR}
            value={
              draftFilters?.createdAt?.from
                ? [draftFilters?.createdAt?.from, draftFilters?.createdAt?.to]
                : null
            }
            onChange={(values) =>
              setDraftFilters({
                ...draftFilters,
                createdAt: {
                  from: values?.[0]?.startOf('day'),
                  to: values?.[1]?.endOf('day'),
                },
              })
            }
          />
        )}
        {dataIndex === 'status' && (
          <Group
            options={USER_FORM_STATUS}
            value={draftFilters?.status}
            onChange={(e) =>
              setDraftFilters({ ...draftFilters, status: e?.target?.value })
            }
          />
        )}
        <Divider className="divider-filter" />
        <div className="d-flex justify-center">
          <Button
            size="small"
            className="reset-button"
            onClick={() => {
              clearFilters();
              applyFilters({ ...filters, [dataIndex]: undefined }, confirm);
            }}
          >
            Reset
          </Button>
          <Button
            size="small"
            className="common-button ok-button"
            type="primary"
            onClick={() =>
              applyFilters({ ...filters, ...draftFilters }, confirm)
            }
          >
            Ok
          </Button>
        </div>
      </div>
    ),
    filterIcon: () =>
      filters?.[dataIndex] === true ||
      filters?.[dataIndex] === false ||
      filters?.[dataIndex]?.from ||
      (dataIndex === 'role' && filters?.role) ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setDraftFilters(filters);
    },
  });

  const columns = [
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 250,
      align: 'left',
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'left' : false,
      className: 'max-width-column',
      render: (_, record) => (
        <>
          {record?.user?.firstName || record?.user?.lastName ? (
            <div className="table-data-with-id">{`${
              record?.user?.firstName || ''
            } ${record?.user?.lastName || ''}`}</div>
          ) : (
            '-'
          )}
          {record?.user?.userNumber && (
            <div className="font-size-12">({record?.user?.userNumber})</div>
          )}
        </>
      ),
    },
    {
      title: 'ROLE',
      dataIndex: 'role',
      key: 'role',
      ellipsis: true,
      width: 140,
      align: 'left',
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'left' : false,
      className: 'max-width-column',
      render: (_, record) => getStaffRoleLabel(record?.role),
      ...filterPopup('role'),
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.user?.email || '-',
    },
    {
      title: 'CONTACT',
      dataIndex: 'contactNo',
      key: 'contactNo',
      ellipsis: true,
      width: 200,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.user?.contactNo || '-',
    },
    {
      title: 'ONBOARDED ON',
      dataIndex: 'createdAt',
      key: 'createdAt',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('createdAt'),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <Badge
          color={
            record?.user?.isActive
              ? STATUS?.ACTIVE?.color
              : STATUS?.INACTIVE?.color
          }
          text={
            record?.user?.isActive
              ? STATUS?.ACTIVE?.label
              : STATUS?.INACTIVE?.label
          }
        />
      ),
      ...filterPopup('status'),
    },
    {
      title: 'IS AVAILABLE',
      dataIndex: 'isServiceable',
      key: 'isServiceable',
      width: 140,
      align: 'left',
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'right' : false,
      render: (_, record) =>
        record?.role === USER_ROLES?.LIBRARIAN?.key ? (
          '-'
        ) : (
          <StaffAvailabilitySwitch
            userId={record?.user?.id}
            isServiceable={record?.isServiceable}
            staffLabel={getStaffRoleLabel(record?.role)}
            onSuccess={() => refetch()}
          />
        ),
    },
  ];

  return (
    <>
      <Portal portalId="header-title-content">
        <div className="portal-header">{MODULES?.LIBRARY_STAFF}</div>
      </Portal>
      <Card className="full-height-without-nav">
        <div className="d-flex justify-end mb-16 flex-wrap">
          <SearchComponent
            className="search-box"
            query={query}
            setQuery={setQuery}
            getData={setSearchTerm}
          />
        </div>
        <CommonTable
          columns={columns}
          data={staffList}
          loadingData={loading}
          rowKey={(record) => record?.id}
        />
      </Card>
    </>
  );
};

export default LibraryStaff;
